import { eachDayOfInterval, format, subDays } from 'date-fns'
import { BarChart3 } from 'lucide-react'
import { useMemo } from 'react'
import { Bar, BarChart, CartesianGrid } from 'recharts'
import { type ChartConfig, ChartContainer } from '@/components/sv/chart'
import { H3 } from '@/components/sv/typographies'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Separator } from '@/components/ui/separator'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'

const getRandomNumber = () => Math.floor(Math.random() * 10) + 1

export const UsageFallback = () => {
  const chartData = useMemo(() => {
    const now = new Date()
    const interval = eachDayOfInterval({
      start: subDays(now, 31),
      end: now,
    })

    return interval.map((date) => ({
      day: format(date, 'MMM dd'),
      value: getRandomNumber(),
    }))
  }, [])

  const chartConfig = useMemo(() => {
    return {
      value: {
        label: 'Spending',
        color: 'var(--accent)',
      },
    }
  }, []) satisfies ChartConfig

  return (
    <Card className="bg-secondary p-0 gap-0">
      <CardHeader className="px-5 py-3 flex flex-row items-center justify-between gap-0">
        <CardTitle>
          <H3 className="flex items-center gap-2 text-sm text-muted-foreground">
            <BarChart3 className="size-4" /> Usage
          </H3>
        </CardTitle>
        <Skeleton className="h-4 w-52" />
      </CardHeader>
      <CardContent className="p-0">
        <Card className="p-0 border-x-0 border-b-0 shadow-none">
          <CardContent className="p-0">
            {/* Filters skeleton */}
            <div className="px-6 py-5 flex items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                <Skeleton className="h-9 w-[160px]" />
                <Skeleton className="h-9 w-[100px]" />
                <Skeleton className="h-9 w-[100px]" />
                <Skeleton className="h-9 w-[110px]" />
              </div>
              <div className="flex items-center gap-2">
                <Skeleton className="h-9 w-[80px]" />
                <Skeleton className="h-9 w-[70px]" />
              </div>
            </div>
            <Separator />
            {/* Summary cards skeleton */}
            <div className="flex">
              {new Array(4).fill(null).map((_, idx) => (
                <div key={idx} className="contents">
                  {idx > 0 && <div className="w-px bg-border" />}
                  <div className="flex-1 min-w-0 px-6 py-4">
                    <Skeleton className="h-3 w-20 mb-2" />
                    <Skeleton className="h-7 w-24" />
                  </div>
                </div>
              ))}
            </div>
            <Separator />
            {/* Chart skeleton */}
            <div className="py-6">
              <div className="px-6 flex items-center justify-end pb-4">
                <div className="flex items-center gap-2">
                  <Skeleton className="h-8 w-[138px] rounded-md" />
                  <Skeleton className="h-8 w-[68px] rounded-md" />
                </div>
              </div>
              <div className="px-6">
                <ChartContainer
                  config={chartConfig}
                  className="min-h-[250px] max-h-[350px] w-full [&_.recharts-responsive-container]:max-h-[350px]"
                >
                  <BarChart accessibilityLayer data={chartData}>
                    <CartesianGrid vertical={false} className="opacity-20" />
                    <Bar
                      dataKey="value"
                      fill="var(--accent)"
                      className="animate-pulse"
                    />
                  </BarChart>
                </ChartContainer>
              </div>
            </div>
            {/* Table skeleton */}
            <div className="py-6">
              <div className="rounded-md border mx-6">
                <Table className="table-fixed">
                  <TableHeader>
                    <TableRow className="bg-muted/50 hover:bg-muted/50">
                      <TableHead className="w-[50%] pl-4">Name</TableHead>
                      <TableHead className="w-[18%] text-right">Net</TableHead>
                      <TableHead className="w-[18%] text-right">
                        Gross
                      </TableHead>
                      <TableHead className="w-[14%] text-right pr-4">
                        % of total
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {new Array(5).fill(null).map((_, idx) => (
                      <TableRow key={idx}>
                        <TableCell className="py-2 pl-4">
                          <Skeleton className="h-4 w-32" />
                        </TableCell>
                        <TableCell className="py-2 text-right">
                          <Skeleton className="ml-auto h-4 w-16" />
                        </TableCell>
                        <TableCell className="py-2 text-right">
                          <Skeleton className="ml-auto h-4 w-16" />
                        </TableCell>
                        <TableCell className="py-2 text-right pr-4">
                          <Skeleton className="ml-auto h-4 w-12" />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          </CardContent>
        </Card>
      </CardContent>
    </Card>
  )
}
